/**
 * References (SPEC §5.3), derived from content by the one parser: `[[T]]` (link), `#t` (tag),
 * `@n` (mention), `{{embed …}}` (embed), `((id))` (link to a block). Text refs are written by
 * title; titles are unique, so a title is as good as an id at any consistent moment, and a rename
 * rewrites the referencing blocks (D4).
 *
 * Rewrites are `node.relink` ops, applied against the content the receiver has at that moment, so
 * a rename never overwrites an edit another device made to a referencing block (D57).
 */
import type { Graph } from './graph.ts'
import type { NodeId } from './ids.ts'
import { parse, walkInlines, type RefKind } from './markdown.ts'
import { makeNode, type Node } from './model.ts'
import { op, type OpBody } from './ops.ts'
import { hasTitle, normalizeTitle, titleKey, titleOwner, titleProblem } from './titles.ts'
import { isUuid } from './validate.ts'

export interface TextRef {
  readonly kind: RefKind | 'embed' | 'block'
  /** Title as written (title refs), or null. */
  readonly title: string | null
  /** Target id (block refs and id embeds), or null. */
  readonly id: string | null
  readonly bracket: boolean
  readonly from: number
  readonly to: number
}

/** Cheap pre-check: content without these characters has no refs. */
export function mayHaveRefs(content: string): boolean {
  return /\[\[|[#@]|\(\(|\{\{/.test(content)
}

/** Every ref in the content, in text order (duplicates included). */
export function refsOf(content: string): TextRef[] {
  if (!mayHaveRefs(content)) return []
  const out: TextRef[] = []
  for (const x of walkInlines(parse(content))) {
    if (x.t === 'ref') out.push({ kind: x.kind, title: x.title, id: null, bracket: x.bracket, from: x.from, to: x.to })
    else if (x.t === 'blockref') out.push({ kind: 'block', title: null, id: x.id, bracket: true, from: x.from, to: x.to })
    else if (x.t === 'embed') out.push({ kind: 'embed', title: x.title, id: x.id, bracket: true, from: x.from, to: x.to })
  }
  return out
}

/**
 * Index keys: `t:<title key>` any text ref to a title, `g:<title key>` a `#tag` to it,
 * `i:<id>` a block ref, embed or field value naming a node, `x:<id>` an explicit tag.
 */
export const keyForTitle = (title: string): string => `t:${titleKey(title)}`
export const keyForTagTitle = (title: string): string => `g:${titleKey(title)}`
export const keyForId = (id: string): string => `i:${id}`
export const keyForExplicitTag = (id: string): string => `x:${id}`

/** The distinct index keys a content refers to. */
export function refKeys(content: string): string[] {
  const keys = new Set<string>()
  for (const r of refsOf(content)) {
    if (r.title !== null) {
      keys.add(keyForTitle(r.title))
      if (r.kind === 'tag') keys.add(keyForTagTitle(r.title))
    }
    if (r.id !== null) keys.add(keyForId(r.id))
  }
  return [...keys]
}

const idValues = (v: unknown): string[] => (Array.isArray(v) ? v.filter(isUuid) : isUuid(v) ? [v] : [])

/** True if a node can refer to anything (text refs, explicit tags, ref field values). */
function mayRefer(n: Node): boolean {
  if (mayHaveRefs(n.content) || n.tags.length > 0) return true
  for (const v of Object.values(n.props)) if (idValues(v).length > 0) return true
  return false
}

/** Every index key of a node: its text refs, its explicit tags and the nodes its fields name. */
export function nodeRefKeys(n: Node): string[] {
  const keys = new Set(refKeys(n.content))
  for (const t of n.tags) keys.add(keyForExplicitTag(t))
  for (const [field, v] of Object.entries(n.props)) {
    if (field.startsWith('sys:')) continue
    for (const id of idValues(v)) keys.add(keyForId(id))
  }
  return [...keys]
}

// ------------------------------------------------------------------------------------ syntax

const BARE_TAG = /^[\p{L}\p{N}\p{M}_\-/+&.]+$/u
const BARE_MENTION = /^[\p{L}\p{N}\p{M}_\-.]+$/u
const bareable = (t: string, re: RegExp): boolean => re.test(t) && !t.endsWith('.') && !/^\p{N}+$/u.test(t)

/** How a ref to `title` is written: `[[T]]`, `#t` / `#[[T u]]`, `@n` / `@[[N m]]`, `{{embed [[T]]}}`. */
export function refSyntax(kind: RefKind | 'embed', title: string, bracket = false): string {
  switch (kind) {
    case 'link':
      return `[[${title}]]`
    case 'tag':
      return !bracket && bareable(title, BARE_TAG) ? `#${title}` : `#[[${title}]]`
    case 'mention':
      return !bracket && bareable(title, BARE_MENTION) ? `@${title}` : `@[[${title}]]`
    case 'embed':
      return `{{embed [[${title}]]}}`
  }
}

/**
 * Rewrite every ref to `from` (any spelling) as a ref to `to`, keeping its kind. Refs inside code
 * stay untouched, because the parser does not see them as refs.
 */
export function relinkContent(content: string, from: string, to: string): string {
  const key = titleKey(from)
  let out = ''
  let last = 0
  for (const r of refsOf(content)) {
    if (r.title === null || r.kind === 'block' || titleKey(r.title) !== key) continue
    out += content.slice(last, r.from) + refSyntax(r.kind, to, r.bracket)
    last = r.to
  }
  return last === 0 ? content : out + content.slice(last)
}

// ------------------------------------------------------------------------------------ graph

/** Live nodes whose content refers to `title`, by scanning (exact; deterministic order by id). */
export function scanReferrers(g: Graph, title: string): NodeId[] {
  const key = titleKey(title)
  const probe = key.split(' ').reduce((a, b) => (b.length > a.length ? b : a), '')
  const out: NodeId[] = []
  for (const n of g.nodes()) {
    if (n.deletedAt !== null || n.content === '' || !mayHaveRefs(n.content)) continue
    const hay = n.content.toLowerCase()
    if (!hay.includes(probe) && !hay.normalize('NFC').includes(probe)) continue
    if (refsOf(n.content).some((r) => r.title !== null && r.kind !== 'block' && titleKey(r.title) === key)) out.push(n.id)
  }
  return out.sort()
}

export type RenameResult = { readonly ok: true; readonly ops: OpBody[] } | { readonly ok: false; readonly error: string }

/** Ops for renaming a page or tag: the title, then a relink for every referencing node. */
export function renameOps(g: Graph, id: NodeId, raw: string): RenameResult {
  const n = g.get(id)
  if (!n || n.deletedAt !== null || n.kind === 'block' || n.kind === 'journal') return { ok: false, error: 'This node has no title.' }
  const title = normalizeTitle(raw)
  const problem = titleProblem(title)
  if (problem) return { ok: false, error: problem }
  if (n.title === title) return { ok: true, ops: [] }
  const other = titleOwner(g, title, id)
  if (other) return { ok: false, error: `“${other.title}” already exists.` }
  const ops: OpBody[] = [op.title(id, title)]
  if (n.title !== null) for (const r of scanReferrers(g, n.title)) ops.push(op.relink(r, n.title, title))
  return { ok: true, ops }
}

export interface LinkPageOptions {
  /** Titles already being created in this transaction or known to exist (keys). */
  readonly resolve: (title: string) => NodeId | null
  readonly newId: () => NodeId
  readonly now: number
  /**
   * Caret offset in `content` while the block is still being edited: the ref around the caret is
   * left alone (SPEC §6.2: nothing is created while the caret is inside the brackets).
   */
  readonly caret?: number | null
  /** Tags for a page created from a ref of this kind (`@name` creates a #person, D13). */
  readonly tagsFor?: (kind: RefKind) => readonly NodeId[]
}

/**
 * Pages to create for refs in `content` whose title does not exist yet (SPEC §6.2: a page is
 * created when you finish `]]` around a new title and leave the brackets; `#x` ≡ `[[x]]`, D25).
 * Returns create ops; titles that cannot be titles are skipped and stay unresolved.
 */
export function linkPageOps(content: string, opts: LinkPageOptions): OpBody[] {
  const ops: OpBody[] = []
  const made = new Set<string>()
  for (const r of refsOf(content)) {
    if (r.title === null || r.kind === 'block') continue
    if (opts.caret !== undefined && opts.caret !== null) {
      const inside = r.bracket ? opts.caret > r.from && opts.caret < r.to : opts.caret > r.from && opts.caret <= r.to
      if (inside) continue
    }
    const title = normalizeTitle(r.title)
    const key = titleKey(title)
    if (made.has(key) || titleProblem(title) !== null || opts.resolve(title) !== null) continue
    made.add(key)
    const tags = opts.tagsFor && r.kind !== 'embed' ? [...opts.tagsFor(r.kind)] : []
    ops.push(op.create(makeNode({ id: opts.newId(), kind: 'page', title, tags, createdAt: opts.now, updatedAt: opts.now })))
  }
  return ops
}

/** True if a page is empty: no live children, no fields, no tags, no content. */
export function isEmptyPage(g: Graph, n: Node): boolean {
  if (n.kind !== 'page' || n.content !== '' || n.tags.length > 0 || Object.keys(n.props).length > 0) return false
  return !g.children(n.id).some((k) => g.isLive(k))
}

/**
 * Referrers by index key, kept current from graph change sets. Built lazily on the first query
 * (parsing every block costs time at startup that the journal does not need).
 */
export class RefIndex {
  private readonly g: Graph
  private built = false
  private readonly out = new Map<NodeId, { node: Node; keys: string[] }>()
  private readonly inn = new Map<string, Set<NodeId>>()
  private readonly listeners = new Set<() => void>()
  private pendingIds: NodeId[] | null = null
  version = 0

  constructor(g: Graph) {
    this.g = g
    g.subscribe((cs) => {
      if (!this.built) return
      let changed = false
      for (const id of cs.nodes) if (this.reindex(id)) changed = true
      if (changed) {
        this.version++
        this.listeners.forEach((f) => f())
      }
    })
  }

  subscribe = (fn: () => void): (() => void) => {
    this.listeners.add(fn)
    return () => this.listeners.delete(fn)
  }

  getVersion = (): number => this.version

  private ensure(): void {
    if (this.built) return
    while (!this.warm(Infinity));
  }

  /**
   * Build part of the index within `budgetMs` (call from idle time); returns true when complete.
   * Changes arriving meanwhile are caught up at the end (the listener only runs once built).
   */
  warm(budgetMs: number): boolean {
    if (this.built) return true
    const start = Date.now()
    this.pendingIds ??= [...this.g.nodes()].filter((n) => n.deletedAt === null && mayRefer(n)).map((n) => n.id)
    const ids = this.pendingIds
    while (ids.length > 0) {
      for (let i = 0; i < 500 && ids.length > 0; i++) this.reindex(ids.pop()!)
      if (Date.now() - start >= budgetMs) return false
    }
    // Catch up with anything that changed while warming: reindex skips unchanged records.
    for (const n of this.g.nodes()) if (n.deletedAt === null ? mayRefer(n) : this.out.has(n.id)) this.reindex(n.id)
    this.pendingIds = null
    this.built = true
    this.version++
    this.listeners.forEach((f) => f())
    return true
  }

  /** Returns true if the node's outgoing refs changed. */
  private reindex(id: NodeId): boolean {
    const n = this.g.get(id)
    const live = n !== undefined && n.deletedAt === null
    const prev = this.out.get(id)
    if (live && prev && prev.node === n) return false
    const keys = live && mayRefer(n) ? nodeRefKeys(n) : []
    if (!prev && keys.length === 0) return false
    if (prev) {
      for (const k of prev.keys) {
        const s = this.inn.get(k)
        s?.delete(id)
        if (s && s.size === 0) this.inn.delete(k)
      }
      this.out.delete(id)
    }
    if (keys.length > 0) {
      this.out.set(id, { node: n!, keys })
      for (const k of keys) {
        let s = this.inn.get(k)
        if (!s) this.inn.set(k, (s = new Set()))
        s.add(id)
      }
    }
    return true
  }

  /** Live nodes referring to the key. */
  referrers(key: string): NodeId[] {
    this.ensure()
    const s = this.inn.get(key)
    if (!s) return []
    return [...s].filter((id) => this.g.isLive(id))
  }

  /** Live nodes referring to a node in any way: its title, its id, or as an explicit tag. */
  referrersOf(n: Node): NodeId[] {
    const ids = new Set([...this.referrers(keyForId(n.id)), ...this.referrers(keyForExplicitTag(n.id))])
    if (hasTitle(n)) for (const id of this.referrers(keyForTitle(n.title!))) ids.add(id)
    return [...ids]
  }

  /** Live nodes carrying the node as a tag: `#title` in their text or in their explicit tags. */
  taggedWith(n: Node): NodeId[] {
    const ids = new Set(this.referrers(keyForExplicitTag(n.id)))
    if (hasTitle(n)) for (const id of this.referrers(keyForTagTitle(n.title!))) ids.add(id)
    return [...ids]
  }
}
